// Phase 2.6 — Partners directory table + filter bar extracted from
// Partners.jsx. Pure presentational; parent owns row data + all action
// callbacks. Sort state is lifted so the orchestrator can persist it across
// remounts.

import { Btn, Input, Section } from "@/components/Shared";
import { Pencil, Trash2, ArrowUp, ArrowDown, ArrowUpDown, Mail, KeyRound } from "lucide-react";
import { fmtPeriod } from "@/lib/api";

function SortHeader({ k, children, align = "left", sortBy, onToggle }) {
  const active = sortBy.key === k;
  const Icon = !active ? ArrowUpDown : (sortBy.dir === "asc" ? ArrowUp : ArrowDown);
  return (
    <th className={align === "right" ? "text-right" : ""}>
      <button
        type="button"
        data-testid={`p-sort-${k}`}
        onClick={() => onToggle(k)}
        className={`inline-flex items-center gap-1 hover:text-[var(--ink)] ${active ? "text-[var(--ink)]" : ""} ${align === "right" ? "flex-row-reverse" : ""}`}
      >
        <span>{children}</span>
        <Icon size={11} strokeWidth={2} className={active ? "text-[var(--accent)]" : "text-[var(--muted)]"} />
      </button>
    </th>
  );
}

export default function PartnersTable({
  filtered, hasFilters,
  q, setQ,
  filterComp, setFilterComp,
  filterGst, setFilterGst,
  sortBy, onToggleSort,
  onClearFilters,
  partnerHasAccess,
  onEmailOne, onEdit, onRemove, onGrant, onViewRateCards,
}) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="partner-filter-bar">
        <div className="w-56">
          <Input placeholder="Search partners…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="partner-search" />
        </div>
        <div className="w-48">
          <select
            data-testid="partner-filter-comp"
            value={filterComp}
            onChange={(e) => setFilterComp(e.target.value)}
            className="w-full h-9 px-3 text-[13.5px] bg-[var(--surface)] border border-[var(--line-2)] rounded-sm focus:outline-none focus:border-[var(--ink)]"
          >
            <option value="">All compensation</option>
            <option value="fixed_base_rate">Fixed base rate</option>
            <option value="revenue_share">Revenue share</option>
            <option value="fixed_fee">Fixed fee</option>
          </select>
        </div>
        <div className="w-40">
          <select
            data-testid="partner-filter-gst"
            value={filterGst}
            onChange={(e) => setFilterGst(e.target.value)}
            className="w-full h-9 px-3 text-[13.5px] bg-[var(--surface)] border border-[var(--line-2)] rounded-sm focus:outline-none focus:border-[var(--ink)]"
          >
            <option value="">GST: any</option>
            <option value="yes">GST applicable</option>
            <option value="no">GST not applicable</option>
          </select>
        </div>
        {hasFilters && <Btn variant="ghost" testid="partner-clear-filters" onClick={onClearFilters}>Clear filters</Btn>}
      </div>

      <Section
        title={`${filtered.length} partner${filtered.length === 1 ? "" : "s"}${hasFilters ? " · filtered" : ""}`}
        testid="partners-section"
      >
        <div className="overflow-x-auto"><table className="table-data w-full">
          <thead><tr>
            <SortHeader k="partner_id" sortBy={sortBy} onToggle={onToggleSort}>ID</SortHeader>
            <SortHeader k="partner_name" sortBy={sortBy} onToggle={onToggleSort}>Partner Name</SortHeader>
            <SortHeader k="compensation" sortBy={sortBy} onToggle={onToggleSort}>Compensation</SortHeader>
            <th>PAN / GSTIN</th>
            <SortHeader k="tds" align="right" sortBy={sortBy} onToggle={onToggleSort}>TDS</SortHeader>
            <SortHeader k="gst" align="right" sortBy={sortBy} onToggle={onToggleSort}>GST</SortHeader>
            <th></th>
          </tr></thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.partner_id} data-testid={`partner-row-${r.partner_id}`}>
                <td className="num font-medium">{r.partner_id}</td>
                <td>
                  <div className="font-medium">{r.partner_name}</div>
                  <div className="text-[11px] text-[var(--muted)] flex items-center gap-2">
                    {r.contact}
                    {(r.standing_deductions || []).filter((s) => Number(s.amount) > 0).length > 0 && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 border border-amber-300 text-amber-800 rounded-sm text-[10px] font-semibold uppercase tracking-wider"
                        title={(r.standing_deductions || []).map((s) => `${s.label}: ₹${Number(s.amount).toLocaleString("en-IN")}/mo${s.start_month ? ` from ${fmtPeriod(s.start_month)}` : ""}`).join("\n")}
                      >
                        <span className="w-1 h-1 bg-amber-500 rounded-full" />
                        {r.standing_deductions.length} deduction{r.standing_deductions.length === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>
                </td>
                <td className="text-[12px]">
                  {r.compensation_model === "revenue_share" ? (
                    <span><span className="font-semibold text-[var(--accent)] num">{((r.revenue_share_pct || 0) * 100).toFixed(2)}%</span> <span className="text-[var(--muted)]">of gross</span></span>
                  ) : r.compensation_model === "fixed_fee" ? (
                    Number(r.fixed_monthly_fee) > 0 ? (
                      <div className="flex flex-col gap-1" data-testid={`p-fixed-fee-${r.partner_id}`}>
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[var(--accent)]/10 border border-[var(--accent)]/30 text-[var(--accent)] rounded-sm text-[11px] font-semibold num w-fit">
                          ₹ {Number(r.fixed_monthly_fee).toLocaleString("en-IN")}<span className="text-[10px] font-normal text-[var(--accent)]/80">/mo</span>
                        </span>
                        <span className="text-[10px] text-[var(--muted)] num">
                          {r.fixed_fee_start_month ? `from ${r.fixed_fee_start_month}` : "no start date"}
                          {r.fixed_fee_end_month ? ` → ${r.fixed_fee_end_month}` : r.fixed_fee_start_month ? " · ongoing" : ""}
                        </span>
                      </div>
                    ) : (
                      <span><span className="font-medium">Fixed fee</span> <span className="text-[var(--muted)]">· set amount in profile</span></span>
                    )
                  ) : (
                    <span>
                      <span className="font-medium">Fixed base rate</span>
                      <span className="text-[var(--muted)]">{" · "}</span>
                      <button
                        type="button"
                        onClick={() => onViewRateCards?.(r)}
                        data-testid={`view-rate-cards-${r.partner_id}`}
                        disabled={!(r.rate_cards?.length)}
                        title={r.rate_cards?.length ? "View rate cards" : "No rate cards yet — use Edit to add one"}
                        className={`text-[12px] ${r.rate_cards?.length ? "text-[var(--accent)] hover:underline cursor-pointer" : "text-[var(--muted)] cursor-default"}`}
                      >
                        {r.rate_cards?.length || 0} card(s)
                      </button>
                    </span>
                  )}
                </td>
                <td className="text-[11px] num">
                  <div>{r.pan || <span className="text-[var(--muted)]">—</span>}</div>
                  <div className="text-[var(--muted)]">{r.gstin || ""}</div>
                </td>
                <td className="text-right num">{((r.tds_rate ?? 0) * 100).toFixed(1)}%</td>
                <td className="text-right num">{r.gst_applicable ? `${((r.gst_rate ?? 0.18) * 100).toFixed(0)}%` : <span className="text-[var(--muted)]">—</span>}</td>
                <td className="text-right">
                  <button
                    onClick={() => onEmailOne(r)}
                    disabled={!r.email}
                    title={r.email ? `Email recon to ${r.email}` : "Add an email on this partner to enable"}
                    className={`p-1.5 rounded-sm ${r.email ? "hover:bg-[var(--surface-2)] text-[var(--accent)]" : "text-[var(--muted)] opacity-40 cursor-not-allowed"}`}
                    data-testid={`email-recon-${r.partner_id}`}
                  >
                    <Mail size={13} />
                  </button>
                  {partnerHasAccess(r) ? (
                    <button onClick={() => onGrant(r)} title={`${r.partner_name} already has portal access — click to view / revoke / re-issue`} className="p-1.5 hover:bg-[var(--surface-2)] rounded-sm text-emerald-600" data-testid={`grant-access-${r.partner_id}`}>
                      <KeyRound size={13} />
                    </button>
                  ) : (
                    <button onClick={() => onGrant(r)} title="Generate portal access" className="p-1.5 hover:bg-[var(--surface-2)] rounded-sm text-[var(--accent)]" data-testid={`grant-access-${r.partner_id}`}>
                      <KeyRound size={13} />
                    </button>
                  )}
                  <button onClick={() => onEdit(r)} data-testid={`partner-edit-${r.partner_id}`} className="p-1.5 hover:bg-[var(--surface-2)] rounded-sm">
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => onRemove(r.partner_id)} className="p-1.5 hover:bg-red-50 text-[var(--negative)] rounded-sm">
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan={7} className="text-center text-[var(--muted)] py-8">{hasFilters ? "No partners match the current filters." : "No partners yet."}</td></tr>}
          </tbody>
        </table></div>
      </Section>
    </>
  );
}
